import type {
  CallOff, CallOffLine, AvailablePartForCallOff, CallOffDeliveryDestination,
} from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';
import PrintableCallOffItems from './PrintableCallOffItems';
import CallOffLinesGrid, { type InlineEditChange } from './CallOffLinesGrid';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { Plus, ArrowRight, Loader2, Printer } from 'lucide-react';

interface CallOffLinesTableProps {
  callOff: CallOff;
  inlineEditMode: boolean;
  inlineEditChanges: Record<number, InlineEditChange>;
  editingLineId: number | null;
  editingField: string | null;
  editValue: string;
  savingEdit: boolean;
  supplierCanEditLines: boolean;
  onQuantityChange: (line: CallOffLine, newQty: string) => void;
  onStartLineEdit: (lineId: number, field: string, currentValue: string) => void;
  onSaveLineEdit: () => void;
  onCancelLineEdit: () => void;
  setEditValue: (v: string) => void;
  removeLineItem: (line: CallOffLine) => void;
  /** Add parts state */
  showAddParts: boolean;
  availableParts: AvailablePartForCallOff[];
  availableDeliveries: CallOffDeliveryDestination[];
  selectedDelivery: string;
  setSelectedDelivery: (v: string) => void;
  addPartsLoading: boolean;
  partQuantities: Record<number, number>;
  setPartQuantities: React.Dispatch<React.SetStateAction<Record<number, number>>>;
  addingPart: boolean;
  handleAddPart: (part: AvailablePartForCallOff) => void;
  isSupplier: boolean;
  /** Per-part PO availability budget. When a warehouse line's part is over,
   *  the qty input goes red and the parent disables Save Changes. */
  overByPart?: Record<string, { effective: number; consumed: number; over: boolean }>;
}

/**
 * The call-off detail modal's Items tab: the lines and their pallet / weight
 * summary (CallOffLinesGrid), plus the print sheet, the supplier's add-parts
 * panel and the call-off notes.
 */
export default function CallOffLinesTable({
  callOff, inlineEditMode, inlineEditChanges,
  editingLineId: _editingLineId, editingField: _editingField,
  editValue: _editValue, savingEdit: _savingEdit,
  supplierCanEditLines: _supplierCanEditLines,
  onQuantityChange,
  onStartLineEdit: _onStartLineEdit, onSaveLineEdit: _onSaveLineEdit,
  onCancelLineEdit: _onCancelLineEdit, setEditValue: _setEditValue,
  removeLineItem,
  showAddParts, availableParts, availableDeliveries,
  selectedDelivery, setSelectedDelivery,
  addPartsLoading, partQuantities, setPartQuantities, addingPart, handleAddPart,
  isSupplier: _isSupplier,
  overByPart,
}: CallOffLinesTableProps) {
  const { formatDate } = useFormatters();

  return (
    <>
      {/* CR-7732: a paper copy of what is on the load, for whoever is loading
          or receiving it. Prints the sheet below, not the screen. */}
      {callOff.lines && callOff.lines.length > 0 && (
        <PrintableCallOffItems
          callOff={callOff}
          formatDate={formatDate}
          generatedBy={null}
        />
      )}
      {callOff.lines && callOff.lines.length > 0 && (
        <div className="flex justify-end mb-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={() => window.print()}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
        </div>
      )}
      {/* CR-7782: the lines and their pallet / weight summary, shared with
          the list's expanded row. */}
      <CallOffLinesGrid
        callOff={callOff}
        inlineEditMode={inlineEditMode}
        inlineEditChanges={inlineEditChanges}
        onQuantityChange={onQuantityChange}
        removeLineItem={removeLineItem}
        overByPart={overByPart}
      />

      {/* Supplier Add Parts Section */}
      {inlineEditMode && showAddParts && (
        <Card className="mt-4 border-blue-200">
          <CardHeader className="py-3 px-4 bg-blue-50">
            <CardTitle className="text-sm flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add New Parts
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 p-2 rounded bg-blue-50 text-blue-700 text-xs">
              <ArrowRight className="h-3.5 w-3.5 shrink-0" />
              Select a warehouse destination, then add products with quantities.
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Warehouse Destination</Label>
              <Select value={selectedDelivery} onValueChange={setSelectedDelivery}>
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Select warehouse..." />
                </SelectTrigger>
                <SelectContent>
                  {availableDeliveries
                    .map((d, idx) => ({ d, idx }))
                    .filter(({ d }) => d.delivery_type === 'warehouse')
                    .map(({ d, idx }) => (
                      <SelectItem key={idx} value={String(idx)}>
                        {d.delivery_destination_name || 'Warehouse'}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {addPartsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : availableParts.length > 0 ? (
              <div className="max-h-[250px] overflow-y-auto border rounded">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Part No</TableHead>
                      <TableHead className="text-xs">Description</TableHead>
                      <TableHead className="text-xs text-right">Load Qty</TableHead>
                      <TableHead className="text-xs text-right w-[80px]">Qty</TableHead>
                      <TableHead className="text-xs text-center w-[70px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {availableParts.map(part => (
                      <TableRow key={part.id}>
                        <TableCell className="font-mono text-xs">{part.part_number}</TableCell>
                        <TableCell className="text-xs">{part.description}</TableCell>
                        <TableCell className="text-xs text-right">{part.load_qty}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            className="h-6 w-[70px] text-right text-xs ml-auto"
                            min={0}
                            value={partQuantities[part.id] || ''}
                            onChange={(e) => setPartQuantities(prev => ({
                              ...prev,
                              [part.id]: parseFloat(e.target.value) || 0,
                            }))}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 text-xs px-2"
                            disabled={addingPart || !selectedDelivery || !(partQuantities[part.id] > 0)}
                            onClick={() => handleAddPart(part)}
                          >
                            Add
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-3">
                No additional parts available to add.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {callOff.notes && (
        <Card className="mt-4">
          <CardHeader className="py-2 px-4">
            <CardTitle className="text-sm">Notes</CardTitle>
          </CardHeader>
          <CardContent className="py-2 px-4 text-sm">{callOff.notes}</CardContent>
        </Card>
      )}
    </>
  );
}
